import React, { useState, useEffect, useRef } from 'react';
import './Menu.css';
import { useAuth } from '../AuthContext';
import Leaderboard from './Leaderboard';

function Menu({ gridSize, setGridSize, difficulty, setDifficulty, onStartGame }) {
  const { currentUser, signInWithGoogle, signOut } = useAuth();
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showAuthDropdown, setShowAuthDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAuthDropdown(false);
      }
    };

    if (showAuthDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAuthDropdown]);

  const handleGoogleLogin = async () => {
    try {
      setShowAuthDropdown(false);
      await signInWithGoogle();
    } catch (error) {
      console.error('Error signing in with Google:', error);
      
      let errorMessage = 'Failed to sign in. ';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in cancelled.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Pop-up blocked. Please allow pop-ups for this site.';
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized. Please add it in Firebase Console.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  return (
    <div className="menu">
      <div className="auth-section">
        {currentUser ? (
          <div className="user-info">
            <img src={currentUser.photoURL} alt="Profile" className="user-avatar" />
            <span className="user-name">{currentUser.displayName}</span>
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        ) : (
          <div className="auth-dropdown-container" ref={dropdownRef}>
            <button 
              className="login-button" 
              onClick={() => setShowAuthDropdown(!showAuthDropdown)}
            >
              Sign In ▼
            </button>
            {showAuthDropdown && (
              <div className="auth-dropdown">
                <button className="auth-option" onClick={handleGoogleLogin}>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                  <span>Sign in with Google</span>
                </button>
                {/* Future auth options can be added here */}
                {/* <button className="auth-option" onClick={handleFacebookLogin}>
                  <img src="facebook-icon.svg" alt="Facebook" />
                  <span>Sign in with Facebook</span>
                </button> */}
              </div>
            )}
          </div>
        )}
      </div>

      <h1 className="game-title">Color Memory Game</h1>
      <p className="game-description">
        Watch the colors appear, then click them in ROYGBV order!
      </p>

      <div className="settings">
        <div className="setting-group">
          <h3>Grid Size</h3>
          <div className="button-group">
            <button
              className={`option-button ${gridSize === '3x3' ? 'active' : ''}`}
              onClick={() => setGridSize('3x3')}
            >
              3x3
            </button>
            <button
              className={`option-button ${gridSize === '4x4' ? 'active' : ''}`}
              onClick={() => setGridSize('4x4')}
            >
              4x4
            </button>
          </div>
        </div>

        <div className="setting-group">
          <h3>Difficulty</h3>
          <div className="button-group">
            <button
              className={`option-button ${difficulty === 'easy' ? 'active' : ''}`}
              onClick={() => setDifficulty('easy')}
            >
              Easy
            </button>
            <button
              className={`option-button ${difficulty === 'medium' ? 'active' : ''}`}
              onClick={() => setDifficulty('medium')}
            >
              Medium
            </button>
            <button
              className={`option-button ${difficulty === 'hard' ? 'active' : ''}`}
              onClick={() => setDifficulty('hard')}
            >
              Hard
            </button>
          </div>
        </div>

        <div className="game-details">
          <h4>Game Settings:</h4>
          <ul>
            <li>Grid: {gridSize}</li>
            <li>Colors: {gridSize === '3x3' ? '3' : '6'}</li>
            <li>Speed: {difficulty}</li>
          </ul>
        </div>

        <div className="menu-buttons">
          <button className="start-button" onClick={onStartGame}>
            Start Game
          </button>
          <button className="leaderboard-button" onClick={() => setShowLeaderboard(true)}>
            🏆 View Leaderboard
          </button>
        </div>
      </div>

      <div className="instructions">
        <h4>How to Play:</h4>
        <ol>
          <li>Watch as colors appear randomly on the grid</li>
          <li>Memorize their positions</li>
          <li>Click them in ROYGBV order (Red, Orange, Yellow, Green, Blue, Violet)</li>
          <li>Complete rounds to increase your score!</li>
        </ol>
      </div>

      {showLeaderboard && <Leaderboard onClose={() => setShowLeaderboard(false)} />}
    </div>
  );
}

export default Menu;
